import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import { pipe } from 'effect/Function';
import { ApiClient } from '@/api/client';
import {
  ExpenseResponseSchema,
  PaginatedExpensesSchema,
  type Expense,
  type PaginatedExpenses,
  type CreateExpenseParams,
  type WeightInput,
  type SplitResult,
} from '@/api/schemas/expense.schema';

export class ExpenseService extends Context.Tag('ExpenseService')<
  ExpenseService,
  {
    readonly create: (params: CreateExpenseParams) => Effect.Effect<Expense, any>;
    readonly fetchForGroup: (
      groupId: string,
      cursor?: string
    ) => Effect.Effect<PaginatedExpenses, any>;
    readonly delete: (expenseId: string) => Effect.Effect<void, any>;
    readonly calculateSplits: (
      amount: number,
      weights: WeightInput[]
    ) => Effect.Effect<SplitResult[], never>;
  }
>() {}

export const ExpenseServiceLive = Layer.effect(
  ExpenseService,
  Effect.gen(function* (_) {
    const api = yield* _(ApiClient);

    return {
      create: (params) => {
        const request = {
          group: params.groupId,
          category: params.categoryId,
          description: params.description,
          amount: params.amount,
          currency_code: params.currency,
          date: params.date,
          funding_pool_id: params.fundingPoolId,
          use_group_pool: params.useGroupPool,
        };

        return pipe(
          api.post('/expenses/create', request, ExpenseResponseSchema),
          Effect.map((response) => response.expense),
          Effect.tap(() =>
            Effect.log(`Expense created: ${params.amount} ${params.currency}`)
          )
        );
      },

      fetchForGroup: (groupId, cursor) => {
        const url = `/groups/${groupId}/expenses${cursor ? `?cursor=${cursor}` : ''}`;
        return pipe(
          api.get(url, PaginatedExpensesSchema),
          Effect.tap((result) =>
            Effect.log(`Fetched ${result.expenses.length} expenses for group ${groupId}`)
          )
        );
      },

      delete: (expenseId) =>
        pipe(
          api.delete(`/expenses/${expenseId}`),
          Effect.tap(() => Effect.log(`Expense deleted: ${expenseId}`))
        ),

      calculateSplits: (amount, weights) =>
        Effect.sync(() => {
          const totalWeight = weights.reduce((sum, w) => sum + w.weight, 0);

          if (totalWeight === 0) {
            // Equal split if no weights
            const equalAmount = amount / weights.length;
            return weights.map((w) => ({
              userId: w.userId,
              amount: equalAmount,
            }));
          }

          // Weighted split
          const splits = weights.map((w) => ({
            userId: w.userId,
            amount: (amount * w.weight) / totalWeight,
          }));

          // Handle rounding errors by adjusting the first split
          const totalSplit = splits.reduce((sum, s) => sum + s.amount, 0);
          const diff = amount - totalSplit;
          if (diff !== 0 && splits.length > 0) {
            splits[0].amount += diff;
          }

          return splits;
        }),
    };
  })
);
